// src/components/layout/AdminPortalHeader.jsx
// Shared dark-green header used by both Admin and Super Admin portals.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { FaBell, FaChevronDown, FaSearch } from "react-icons/fa";

export default function AdminPortalHeader({ onMenuToggle, roleLabel, profilePath, settingsPath, activityLogPath }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [profileOpen, setProfileOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);

  const profileRef = useRef(null);
  const notifyRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
      if (notifyRef.current && !notifyRef.current.contains(e.target)) setNotifyOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const name = `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.username || "Admin";
  const initials = name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "A";

  return (
    <header className="h-[72px] w-full bg-[#0d3d2e] flex items-center px-4 sm:px-6 shrink-0">
      <div className="flex items-center w-full gap-4">
        <button onClick={onMenuToggle} className="md:hidden text-white text-xl">
          ☰
        </button>

        <div className="hidden sm:flex items-center flex-1 max-w-xl">
          <div className="relative w-full">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 w-3.5 h-3.5" />
            <input
              type="text"
              placeholder="Search users, transactions, references..."
              className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/10 rounded-xl text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition"
            />
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <div className="relative" ref={notifyRef}>
            <button
              onClick={() => { setNotifyOpen(!notifyOpen); setProfileOpen(false); }}
              className="relative p-2.5 rounded-full hover:bg-white/10 text-white transition"
            >
              <FaBell className="w-4 h-4" />
              <span className="absolute top-1.5 right-2 w-1.5 h-1.5 rounded-full bg-orange-400" />
            </button>
            {notifyOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-lg z-50">
                <div className="px-4 py-3 border-b font-semibold text-sm text-gray-900">Notifications</div>
                <div className="px-4 py-3 text-sm text-gray-500">No new notifications</div>
              </div>
            )}
          </div>

          <div className="relative" ref={profileRef}>
            <button
              onClick={() => { setProfileOpen(!profileOpen); setNotifyOpen(false); }}
              className="flex items-center gap-2.5 pl-1 pr-3 py-1 rounded-full bg-white/10 hover:bg-white/15 transition"
            >
              <div className="w-8 h-8 bg-green-400 text-[#0d3d2e] rounded-full flex items-center justify-center text-xs font-bold shrink-0">
                {initials}
              </div>
              <span className="hidden sm:inline text-sm font-semibold text-white">{roleLabel}</span>
              <FaChevronDown className="w-2.5 h-2.5 text-white/70" />
            </button>

            {profileOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-lg z-50">
                <div className="px-4 py-3 border-b">
                  <p className="text-sm font-semibold text-gray-800">{name}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
                <button onClick={() => { navigate(profilePath); setProfileOpen(false); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">
                  View Profile
                </button>
                <button onClick={() => { navigate(settingsPath); setProfileOpen(false); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">
                  Account Settings
                </button>
                <button onClick={() => { navigate(activityLogPath); setProfileOpen(false); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">
                  Login Activity
                </button>
                <div className="border-t">
                  <button onClick={handleLogout} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
